export type AdobeBannerDecision = {
  bannerId?: string;
  bannerPriority?: number;
  ctaPath?: string;
  ctaModal?: string;
  navigateBeforeModal?: string;
  /** Pages where {@link AdobeBanner} may render the row; omitted = dashboard only. */
  bannerToShowOn?: string[];
  /**
   * Optional Card Shield dashboard ribbon targeting (Adobe `adobeBannerTest` row).
   * When either bound is set, the row is eligible only if the member’s linked transaction account count
   * is within `[cardShieldLinkedAccountsMin, cardShieldLinkedAccountsMax]` (inclusive).
   * When both are omitted, any account count matches (Target backward compatible).
   */
  cardShieldLinkedAccountsMin?: number;
  cardShieldLinkedAccountsMax?: number;
};

/** Minimal Plaid connection shape from finance-connections (accounts nested per connection). */
type ConnectionWithAccounts = {
  institutionStatusKey?: string;
  accounts?: unknown[];
};

/**
 * Counts linked financial accounts under ACTIVE unified (Plaid) connections — same source as FM unified flows.
 */
export function countActiveTransactionLinkedAccountsFromConnections(
  connections: ConnectionWithAccounts[] | null | undefined
): number {
  if (!Array.isArray(connections)) {
    return 0;
  }
  let total = 0;
  for (const connection of connections) {
    if (connection?.institutionStatusKey === 'ACTIVE' && Array.isArray(connection.accounts)) {
      total += connection.accounts.length;
    }
  }
  return total;
}

/**
 * @param linkedAccountCount — unified: sum of accounts on ACTIVE connections; legacy Yodlee: `getConnectedAccountsCount`
 */
export function cardShieldAdobeBannerMatchesLinkedAccountCount(
  banner: Pick<AdobeBannerDecision, 'cardShieldLinkedAccountsMin' | 'cardShieldLinkedAccountsMax'>,
  linkedAccountCount: number
): boolean {
  const minBound = banner.cardShieldLinkedAccountsMin;
  const maxBound = banner.cardShieldLinkedAccountsMax;
  if (minBound === undefined && maxBound === undefined) {
    return true;
  }
  if (typeof minBound === 'number' && linkedAccountCount < minBound) {
    return false;
  }
  if (typeof maxBound === 'number' && linkedAccountCount > maxBound) {
    return false;
  }
  return true;
}

/** Adobe `bannerId` values for Card Shield dashboard ribbons that DSP routes by linked transaction account count. */
export const CARD_SHIELD_DASHBOARD_BANNER_ID_ENROLLED = 'card-shield-dashboard-banner-enrolled';
export const CARD_SHIELD_DASHBOARD_BANNER_ID_NOT_ENROLLED = 'card-shield-dashboard-banner-not-enrolled';

/**
 * When Target includes the enrolled / not-enrolled dashboard pair (by `bannerId`), keep exactly one:
 * - **≥ 1** linked transaction account → {@link CARD_SHIELD_DASHBOARD_BANNER_ID_ENROLLED}
 * - **0** accounts → {@link CARD_SHIELD_DASHBOARD_BANNER_ID_NOT_ENROLLED}
 *
 * Other rows are unchanged. If neither keyed `bannerId` is present, returns `banners` as-is.
 * Call only after `linkedAccountsReady` is true (same moment as min/max Card Shield filtering).
 */
export function filterCardShieldDashboardAccountKeyedBanners<T extends {bannerId?: string}>(
  banners: T[],
  linkedAccountCount: number,
  linkedAccountsReady: boolean
): T[] {
  if (!linkedAccountsReady || !Array.isArray(banners) || banners.length === 0) {
    return banners;
  }
  const hasKeyed = banners.some(
    (row) =>
      row?.bannerId === CARD_SHIELD_DASHBOARD_BANNER_ID_ENROLLED ||
      row?.bannerId === CARD_SHIELD_DASHBOARD_BANNER_ID_NOT_ENROLLED
  );
  if (!hasKeyed) {
    return banners;
  }
  const wantEnrolled = linkedAccountCount >= 1;
  return banners.filter((row) => {
    const id = row?.bannerId;
    if (id !== CARD_SHIELD_DASHBOARD_BANNER_ID_ENROLLED && id !== CARD_SHIELD_DASHBOARD_BANNER_ID_NOT_ENROLLED) {
      return true;
    }
    if (wantEnrolled) {
      return id === CARD_SHIELD_DASHBOARD_BANNER_ID_ENROLLED;
    }
    return id === CARD_SHIELD_DASHBOARD_BANNER_ID_NOT_ENROLLED;
  });
}

const CARD_SHIELD_MODAL_IDS = new Set(['CARD_SHIELD_DASHBOARD_BANNER_INTRO_MODAL', 'CARD_SHIELD_HOW_IT_WORKS_MODAL']);

function normalizePath(pathValue?: string): string {
  return typeof pathValue === 'string' ? pathValue.trim() : '';
}

function isCardShieldPath(pathValue?: string): boolean {
  return normalizePath(pathValue).startsWith('/card-shield');
}

export function isCardShieldBannerDecision(banner?: AdobeBannerDecision): boolean {
  if (!banner) {
    return false;
  }

  return (
    CARD_SHIELD_MODAL_IDS.has(banner.ctaModal || '') ||
    isCardShieldPath(banner.navigateBeforeModal) ||
    isCardShieldPath(banner.ctaPath)
  );
}

export function getHighestPriorityBannerDecision<T extends AdobeBannerDecision>(banners: T[]): T | undefined {
  if (!Array.isArray(banners) || banners.length === 0) {
    return undefined;
  }

  return [...banners].sort((left, right) => {
    const leftPriority = typeof left?.bannerPriority === 'number' ? left.bannerPriority : Number.MAX_SAFE_INTEGER;
    const rightPriority = typeof right?.bannerPriority === 'number' ? right.bannerPriority : Number.MAX_SAFE_INTEGER;
    return leftPriority - rightPriority;
  })[0];
}

export function getHighestPriorityCardShieldBannerDecision<T extends AdobeBannerDecision>(banners: T[]): T | undefined {
  return getHighestPriorityBannerDecision(banners.filter((banner) => isCardShieldBannerDecision(banner)));
}
